import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePrefs } from '../contexts/PrefsContext';
import { usePlay } from '../contexts/PlayContext';
import { games, categories } from '../data/games';
import GameListing from './GameListing';

function AllGamesList() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const { favorites, addFavorite, removeFavorite } = usePrefs();
  const { gamesPlayedToday, handleGameLinkClick } = usePlay();

  const toggleFavorite = (id, newValue) => {
    if (newValue) addFavorite(id);
    else removeFavorite(id);
  }

  // Default order: linked (auto-scoring) games first, then alphabetical. No sorting UI.
  const orderedGames = useMemo(() => {
    return [...games].sort((g1, g2) => {
      if (g1.isLinked && !g2.isLinked) return -1;
      if (!g1.isLinked && g2.isLinked) return 1;
      return g1.name.localeCompare(g2.name);
    });
  }, []);

  const filteredGames = useMemo(() => {
    let list = orderedGames;
    if (filter === 'new') list = list.filter(g => g.isNew);
    else if (filter === 'autoscore') list = list.filter(g => g.isLinked);
    else if (filter !== 'all') list = list.filter(g => g.category === filter);

    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(g =>
        g.name.toLowerCase().includes(term) ||
        (g.caption || '').toLowerCase().includes(term)
      );
    }
    return list;
  }, [orderedGames, filter, search]);

  // "All" / "New" / "AutoScore" aren't tied to a category, so they use the
  // same cream tone as the page heading rather than a category color.
  const CREAM = '#fef9ef';

  // Hardcoded descriptions for the non-category filters, written to be about
  // the same length as the category descriptions in games.js.
  const NON_CATEGORY_DESCRIPTIONS = {
    all: 'Every game in the catalog, across all categories',
    new: "Recently added games you haven't tried yet",
    autoscore: 'Games that automatically log your score for you',
  };

  const filterDescription = filter === 'all' || filter === 'new' || filter === 'autoscore'
    ? NON_CATEGORY_DESCRIPTIONS[filter]
    : categories.find(cat => cat.id === filter)?.description;

  const chipClassName = 'flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors duration-150 hover:opacity-80';

  const chipStyle = (color, isActive) => isActive
    ? { backgroundColor: color, borderColor: color, color: '#000' }
    : {
        backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)`,
        borderColor: `color-mix(in srgb, ${color} 45%, transparent)`,
        color
      };

  // Left/right scroll arrows for the category chip row
  const chipRowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateChipScrollState = useCallback(() => {
    const el = chipRowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateChipScrollState();
    const el = chipRowRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateChipScrollState, { passive: true });
    window.addEventListener('resize', updateChipScrollState);
    return () => {
      el.removeEventListener('scroll', updateChipScrollState);
      window.removeEventListener('resize', updateChipScrollState);
    };
  }, [updateChipScrollState]);

  const scrollChips = (direction) => {
    const el = chipRowRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  const chipArrowClass = (enabled) =>
    `flex items-center justify-center text-4xl font-bold leading-none transition-opacity duration-150 ${
      enabled
        ? 'text-nlg-text'
        : 'text-nlg-muted opacity-40 cursor-not-allowed'
    }`;

  return (
    <div className="max-w-4xl mx-auto px-1">

      <div className="sticky top-0 z-10 pb-2 bg-nlg-bg">
        <div className="flex items-center gap-3 px-1">
          <Link
            to="/"
            className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-white/[0.06] border border-nlg-rule"
            aria-label="Back to home"
          >
            <img src="/img/left_arrow.png" alt="" className="w-4 h-4" />
          </Link>
          <h1 className="font-display font-black text-xl text-nlg-cream flex-1 text-left">Browse Games</h1>
        </div>

        <div className="px-1 mt-1">
          <div className="flex items-center gap-2 bg-white/[0.06] border border-nlg-rule rounded-xl px-3 py-2.5">
            <span className="text-nlg-muted text-sm">🔍</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or description…"
              className="flex-1 bg-transparent outline-none text-sm text-nlg-text placeholder:text-nlg-muted"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="text-nlg-muted text-sm w-5 h-5 flex items-center justify-center rounded-full bg-white/10"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="relative mt-2">
          <div ref={chipRowRef} className="flex gap-1.5 overflow-x-auto mx-6 px-1 pb-1" style={{ scrollbarWidth: 'none' }}>
            <button onClick={() => setFilter('all')} className={chipClassName} style={chipStyle(CREAM, filter === 'all')}>All</button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setFilter(cat.id)} className={chipClassName} style={chipStyle(cat.color, filter === cat.id)}>
                {cat.shortLabel}
              </button>
            ))}
            <button onClick={() => setFilter('new')} className={chipClassName} style={chipStyle(CREAM, filter === 'new')}>✦ New</button>
            <button onClick={() => setFilter('autoscore')} className={chipClassName} style={chipStyle(CREAM, filter === 'autoscore')}>🔌 AutoScore</button>
          </div>

          {/* Left arrow, with a fade behind it so chips don't clash */}
          <div className="absolute left-0 top-0 bottom-1 w-12 flex items-center bg-gradient-to-r from-nlg-bg via-nlg-bg to-transparent pl-1 pointer-events-none">
            <button
              onClick={() => canScrollLeft && scrollChips(-1)}
              disabled={!canScrollLeft}
              className={`${chipArrowClass(canScrollLeft)} pointer-events-auto`}
              aria-label="Scroll categories left"
            >
              ‹
            </button>
          </div>

          {/* Right arrow, with a fade behind it so chips don't clash */}
          <div className="absolute right-0 top-0 bottom-1 w-12 flex items-center justify-end bg-gradient-to-l from-nlg-bg via-nlg-bg/90 to-transparent pr-1 pointer-events-none">
            <button
              onClick={() => canScrollRight && scrollChips(1)}
              disabled={!canScrollRight}
              className={`${chipArrowClass(canScrollRight)} pointer-events-auto`}
              aria-label="Scroll categories right"
            >
              ›
            </button>
          </div>
        </div>

        {filterDescription && (
          <p className="text-sm text-nlg-cream leading-snug mt-2 px-1">{filterDescription}</p>
        )}

        {/* Fade below the sticky block so scrolled listings fade out instead of clipping on a hard edge */}
        <div className="absolute left-0 right-0 top-full h-[20px] bg-gradient-to-b from-nlg-bg to-transparent pointer-events-none" />
      </div>

      {filteredGames.length > 0 ? (
        <ul className="list-none flex flex-col gap-2 px-1 pb-4 pt-1">
          {filteredGames.map(game => {
            const isFavorite = favorites.includes(game.id);
            const dimGame = gamesPlayedToday.includes(game.id);
            return (
              <GameListing
                key={game.id}
                game={game}
                isFavorite={isFavorite}
                toggleFavorite={toggleFavorite}
                dimGame={dimGame}
                onGameLinkClick={handleGameLinkClick}
              />
            );
          })}
        </ul>
      ) : (
        <div className="text-center py-16 px-4">
          <div className="text-4xl mb-3">🔦</div>
          <h3 className="font-display font-bold text-lg text-nlg-cream mb-2">Nothing found</h3>
          <p className="text-sm text-nlg-muted">Try different search terms or clear your filters.</p>
        </div>
      )}
    </div>
  );
}

export default AllGamesList;
